/**
 * MOB-2 · Escala y encuadre responsive del canvas. Puro y sin DOM (salvo los
 * dos lectores del final, que son los únicos que tocan `window`).
 *
 * El problema que resuelve no es que el canvas no ocupe la pantalla —eso ya lo
 * hacía— sino que el ZOOM estaba en unidades de mundo fijas (`ZOOM_LEVELS`),
 * calibradas mirando una ventana de escritorio. En un móvil en horizontal, con
 * ~390 px de alto útil, un zoom de 1.0 enseña 390 px de una cancha de 680 de
 * alto: no se ven las porterías. El disco desaparece por arriba y por abajo y el
 * juego es literalmente injugable, sin que nada parezca roto.
 *
 * La regla es la de cualquier juego de campo visto desde arriba: el zoom que
 * pide el jugador se ACOTA por lo que la pantalla permite, en los DOS ejes.
 *
 *  - De ALTO hay que ver la cancha entera —las dos bandas y las dos bocas de
 *    portería— o no se puede jugar.
 *  - De ANCHO hay que ver al menos media cancha (`MIN_VISIBLE_COURT_FRACTION`),
 *    o no se ve al rival. Esto faltaba, y en móvil vertical era fatal: el alto
 *    sobraba, así que el techo no mordía nunca y el juego enseñaba una franja de
 *    400 px de un campo de 1560 — con el rival permanentemente fuera de cuadro.
 */

/** Margen de mundo visible más allá de la línea de banda (el marco pintado). */
export const VIEW_MARGIN = 40;

/** Topes absolutos del zoom. Por debajo del mínimo el disco es un píxel. */
export const MIN_ZOOM = 0.28;
export const MAX_ZOOM = 2.5;

/** Densidad de píxeles máxima que se rasteriza. Más allá de 2 el coste crece
 *  con el cuadrado y la mejora ya no se ve. */
export const MAX_DPR = 2;

export interface ViewportMetrics {
  /** Ancho útil en px CSS. */
  width: number;
  /** Alto útil en px CSS. */
  height: number;
  /** Densidad de píxeles a rasterizar, ya acotada. */
  dpr: number;
}

/**
 * Zoom con el que la cancha entera (más su marco) cabe en el viewport.
 * Es el que usa la vista "todo el campo".
 */
export function fitZoom(
  viewW: number,
  viewH: number,
  courtW: number,
  courtH: number,
  margin: number = VIEW_MARGIN,
): number {
  const w = courtW + margin * 2;
  const h = courtH + margin * 2;
  if (viewW <= 0 || viewH <= 0 || w <= 0 || h <= 0) return 1;
  return Math.min(viewW / w, viewH / h);
}

/**
 * NIVELES DE CÁMARA, expresados en **cuánta cancha se ve** y no en una escala
 * absoluta.
 *
 * Es la corrección de fondo del encuadre. Con `zoom = 1.4` (un número de
 * píxeles por unidad de mundo) "CERCA" significaba cosas distintas en cada
 * pantalla: en un monitor ancho se veía media cancha y en un teléfono en
 * vertical una rendija de 400 px con el rival permanentemente fuera de cuadro.
 * Midiendo el nivel en FRACCIÓN DE CAMPO VISIBLE, "MEDIO" enseña la misma
 * jugada en cualquier dispositivo y el zoom sale de la aritmética de cada
 * pantalla.
 *
 * Cada nivel dice el mínimo que hay que ver de ancho y de alto:
 *
 *  - LEJOS: la cancha entera. La vista de "leer el partido".
 *  - MEDIO: todo el alto (las dos porterías) y ~2/3 del largo. Es el defecto.
 *  - CERCA: de verdad cerca — se renuncia a parte del alto, que es lo que antes
 *    lo impedía: el techo vertical recortaba "CERCA" a lo mismo que "MEDIO" en
 *    cualquier monitor de 900 px, y por eso no se notaba la diferencia.
 */
export const ZOOM_VIEWS: readonly { w: number; h: number }[] = [
  { w: 1, h: 1 },
  { w: 0.62, h: 1 },
  { w: 0.45, h: 0.66 },
];

/** Nombres de los niveles, en el mismo orden (los usa la pantalla de ajustes). */
export const ZOOM_LABELS = ["LEJOS", "MEDIO", "CERCA"] as const;

/**
 * Zoom para un nivel de cámara en una pantalla y una cancha concretas: el mayor
 * que sigue enseñando las fracciones que pide el nivel, acotado a los topes
 * absolutos.
 */
export function zoomForLevel(
  level: number,
  view: { width: number; height: number },
  court: { courtW: number; courtH: number },
  margin: number = VIEW_MARGIN,
): number {
  const v = ZOOM_VIEWS[Math.max(0, Math.min(ZOOM_VIEWS.length - 1, Math.round(level)))];
  // El margen pintado cuenta solo cuando se pide la cancha ENTERA: si no, se
  // estaría reservando sitio para el marco en una vista que ni lo enseña.
  const needW = court.courtW * v.w + (v.w >= 1 ? margin * 2 : 0);
  const needH = court.courtH * v.h + (v.h >= 1 ? margin * 2 : 0);
  if (view.width <= 0 || view.height <= 0 || needW <= 0 || needH <= 0) return 1;
  const z = Math.min(view.width / needW, view.height / needH);
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
}

/**
 * Cuánta cancha, A LO ANCHO, hay que ver como mínimo (fracción del campo).
 *
 * Es el arreglo del bug más grave que ha tenido el juego en móvil: el zoom solo
 * se acotaba por ALTO, y en un teléfono en vertical el alto sobra (900 px de
 * pantalla para 680 de cancha) mientras el ancho falta (400 px de pantalla para
 * 1560 de cancha). Resultado: se veía una franja vertical de 400 px de campo, el
 * rival salía de saque a 440 px y **no se le veía nunca** — ni al empezar, ni
 * al perseguir la pelota. El jugador de móvil describía justo eso: "el
 * contrincante aparece invisible".
 *
 * Con esta cota, el zoom se recorta hasta que entra al menos esta fracción del
 * campo de ancho. No es "ver la cancha entera": la mitad larga es suficiente
 * para ver al rival y la jugada, y acercar más sigue siendo elección del
 * jugador dentro de ese techo.
 */
export const MIN_VISIBLE_COURT_FRACTION = 0.5;

/**
 * Zoom máximo que sigue dejando ver esa fracción de la cancha **de ancho**.
 */
export function horizontalFitZoom(
  viewW: number,
  courtW: number,
  fraction: number = MIN_VISIBLE_COURT_FRACTION,
): number {
  const w = courtW * Math.max(0.05, Math.min(1, fraction));
  if (viewW <= 0 || w <= 0) return MAX_ZOOM;
  return viewW / w;
}

/**
 * Zoom máximo que sigue dejando ver la cancha **de alto**. Es el techo real en
 * una pantalla apaisada y baja: pasarse de aquí es recortar las porterías.
 */
export function verticalFitZoom(
  viewH: number,
  courtH: number,
  margin: number = VIEW_MARGIN,
): number {
  const h = courtH + margin * 2;
  if (viewH <= 0 || h <= 0) return MAX_ZOOM;
  return viewH / h;
}

/**
 * Zoom efectivo: el que pidió el jugador, acotado a lo que la pantalla permite.
 *
 * En escritorio no cambia nada (una ventana normal da de sobra para el alto de
 * la cancha, así que el techo queda por encima de cualquier nivel elegible y el
 * `min` no muerde). En un móvil sí: el nivel elegido se recorta hasta que el
 * campo entra de alto. Y el suelo absoluto evita que una pantalla ridículamente
 * baja —o un `courtH` enorme— acabe en un zoom de cero.
 */
export function effectiveZoom(
  preferred: number,
  view: { width: number; height: number },
  court: { courtW: number; courtH: number },
  margin: number = VIEW_MARGIN,
): number {
  // Los DOS techos: el alto (ver las porterías) y el ancho (ver al rival). El
  // que muerda primero manda; en escritorio no muerde ninguno.
  const ceiling = Math.min(
    verticalFitZoom(view.height, court.courtH, margin),
    horizontalFitZoom(view.width, court.courtW),
  );
  const z = Math.min(preferred, ceiling);
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
}

/**
 * ¿Es una pantalla tan estrecha que conviene el mando táctil y una UI compacta?
 * El umbral va en px CSS del lado corto, que es lo que de verdad limita.
 */
export function isCompactViewport(view: { width: number; height: number }): boolean {
  return Math.min(view.width, view.height) < 500;
}

/**
 * Tamaño del buffer del canvas para un viewport y una escala de render dados.
 *
 * Se devuelven las dos cosas por separado a propósito: `cssWidth`/`cssHeight`
 * son los px de maquetación (lo que ocupa en la página) y `width`/`height` los
 * píxeles reales que se rasterizan. Confundirlos es el clásico canvas borroso.
 */
export function canvasSize(
  view: ViewportMetrics,
  renderScale: number,
): { width: number; height: number; cssWidth: number; cssHeight: number; transform: number } {
  const eff = view.dpr * renderScale;
  return {
    width: Math.max(1, Math.floor(view.width * eff)),
    height: Math.max(1, Math.floor(view.height * eff)),
    cssWidth: view.width,
    cssHeight: view.height,
    transform: eff,
  };
}

/* --------------------------- lectores del navegador ----------------------- */

/**
 * Mide el viewport realmente visible.
 *
 * Usa `visualViewport` cuando existe porque en un móvil `innerHeight` MIENTE: no
 * descuenta la barra de direcciones que el navegador esconde y enseña al hacer
 * scroll, así que el canvas se dimensiona más alto que el hueco visible y la
 * franja de abajo —donde va el botón de patada— queda debajo de la barra del
 * navegador, fuera del alcance del pulgar.
 */
export function readViewport(win: Window = window): ViewportMetrics {
  const vv = (win as Window & { visualViewport?: { width: number; height: number } }).visualViewport;
  const width = Math.max(1, Math.round(vv?.width ?? win.innerWidth));
  const height = Math.max(1, Math.round(vv?.height ?? win.innerHeight));
  return {
    width,
    height,
    dpr: Math.min(win.devicePixelRatio || 1, MAX_DPR),
  };
}

/**
 * Se suscribe a TODO lo que cambia el tamaño útil: rotar el móvil, redimensionar
 * la ventana y que el navegador esconda o enseñe su barra (que no dispara
 * `resize` en todos los navegadores, solo `visualViewport.resize`).
 */
export function onViewportChange(cb: () => void, win: Window = window): () => void {
  const vv = (win as Window & {
    visualViewport?: {
      addEventListener: (t: string, f: () => void) => void;
      removeEventListener: (t: string, f: () => void) => void;
    };
  }).visualViewport;
  win.addEventListener("resize", cb);
  win.addEventListener("orientationchange", cb);
  vv?.addEventListener("resize", cb);
  vv?.addEventListener("scroll", cb);
  return () => {
    win.removeEventListener("resize", cb);
    win.removeEventListener("orientationchange", cb);
    vv?.removeEventListener("resize", cb);
    vv?.removeEventListener("scroll", cb);
  };
}
